import React from "react";
import { Moon } from "lucide-react";
import { Button } from "../../ui/button";

/**
 * Inline "going dark" panel — pauses a channel for 1/2/4 weeks
 * with optional auto-renewal.
 *
 * Props:
 *   target          — { id, username } or null when hidden
 *   weekOptions     — [1, 2, 4] from config
 *   costPerWeek     — credits per week of darkness
 *   weeks, onWeeks  — currently-selected week count
 *   autoRenew, onAutoRenew — checkbox state
 *   busy            — POST /pause in flight
 *   onSubmit, onCancel
 */
const ChannelPausePanel = ({
  target, weekOptions, costPerWeek,
  weeks, onWeeks, autoRenew, onAutoRenew, busy, onSubmit, onCancel,
}) => {
  if (!target) return null;
  return (
    <div className="bg-amber-500/5 border border-amber-500/30 rounded-lg p-3 mb-4" data-testid="pause-panel">
      <p className="text-amber-200 text-sm font-medium mb-2 flex items-center gap-2">
        <Moon className="h-4 w-4" /> Going dark on /{target.username}
      </p>
      <p className="text-xs text-gray-400 mb-3">
        Hides this channel from feeds, maps, and search. Direct profile URL still works
        so existing share links survive.
      </p>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {weekOptions.map((w) => (
          <button
            key={w}
            type="button"
            onClick={() => onWeeks(w)}
            className={`px-3 py-1.5 rounded text-xs font-medium border ${
              weeks === w
                ? "bg-amber-500 border-amber-400 text-black"
                : "bg-gray-900 border-gray-700 text-gray-300 hover:border-gray-600"
            }`}
            data-testid={`pause-weeks-${w}`}
          >
            {w} {w === 1 ? "week" : "weeks"} · {w * costPerWeek} cr
          </button>
        ))}
      </div>
      <label className="flex items-center gap-2 text-xs text-gray-300 mb-3">
        <input
          type="checkbox"
          checked={autoRenew}
          onChange={(e) => onAutoRenew(e.target.checked)}
          data-testid="pause-auto-renew"
        />
        Auto-renew every {weeks} {weeks === 1 ? "week" : "weeks"} until I resume or run out of credits
      </label>
      <div className="flex gap-2">
        <Button
          onClick={onSubmit}
          disabled={busy}
          className="bg-amber-600 hover:bg-amber-700 text-xs"
          data-testid="pause-confirm"
        >
          {busy ? "Going dark..." : `Confirm · ${weeks * costPerWeek} credits`}
        </Button>
        <Button variant="outline" onClick={onCancel} className="border-gray-700 text-gray-300 text-xs">
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default ChannelPausePanel;
